// Meta insights.  /analytics/meta
//
// Messaging, pricing, template and call reporting straight from the Meta API,
// four tabs over one endpoint.
//
// The volume-vs-cost pair is drawn as two charts rather than one grouped pair.
// Messages and dollars do not share a unit, so a shared axis was claiming a
// comparison that does not exist. No series is dropped.

import { useMemo, useState } from "react";

import { http, qs } from "../api";
import {
  Badge,
  Banner,
  Empty,
  Search,
  SkeletonRows,
  Spinner,
  Table,
  Tabs,
  useAsync,
  useToast,
  type Column,
} from "../ui";
import { BarChartCard, LineChartCard, StatTile } from "../charts";
import {
  KeyCount,
  NotMeasuredTile,
  RangeControls,
  errText,
  money,
  nf,
  useRange,
} from "./shared";

type MetaAnalyticsType = "analytics" | "pricing_analytics" | "template_analytics" | "call_analytics";
type MetaGranularity = "HALF_HOUR" | "DAY" | "MONTH";

interface MetaAccount {
  id: string;
  name: string;
  phone_id: string;
}

interface MetaMessagingPoint {
  start: number;
  end: number;
  sent: number;
  delivered: number;
}

interface MetaPricingPoint {
  start: number;
  end: number;
  volume: number;
  cost: number;
  country?: string;
  pricing_type?: string;
  pricing_category?: string;
  tier?: string;
}

interface MetaTemplateCostItem {
  type: string;
  value?: number;
}

interface MetaTemplateClickItem {
  type?: string;
  button_content?: string;
  count: number;
}

interface MetaTemplatePoint {
  start: number;
  end: number;
  template_id: string;
  sent: number;
  delivered: number;
  read: number;
  replied?: number;
  clicked?: MetaTemplateClickItem[];
  cost?: MetaTemplateCostItem[];
}

interface MetaCallPoint {
  start: number;
  end: number;
  total_calls: number;
  call_duration: number;
  call_type: string;
  call_direction: string;
}

interface MetaAccountData {
  id: string;
  analytics?: { granularity: string; data_points: MetaMessagingPoint[] };
  pricing_analytics?: { granularity: string; data_points: MetaPricingPoint[] };
  template_analytics?: { granularity: string; data_points: MetaTemplatePoint[] };
  call_analytics?: { granularity: string; data_points: MetaCallPoint[] };
}

interface MetaAccountResult {
  account_id: string;
  account_name: string;
  data: MetaAccountData | null;
  template_names?: Record<string, string>;
}

interface MetaAnalyticsResponse {
  accounts: MetaAccountResult[];
  cached?: boolean;
  message?: string;
  adjusted_granularity?: string;
  original_granularity?: string;
}

interface MetaAccountsResponse {
  accounts: MetaAccount[];
}

interface TemplateRow {
  id: string;
  templateId: string;
  name: string;
  sent: number;
  delivered: number;
  read: number;
  replied: number;
  clicked: number;
  cost: number;
  deliveryRate: number;
  readRate: number;
}

const META_TABS: { id: MetaAnalyticsType; label: string }[] = [
  { id: "analytics", label: "Messaging" },
  { id: "pricing_analytics", label: "Pricing" },
  { id: "template_analytics", label: "Templates" },
  { id: "call_analytics", label: "Calls" },
];

const GRANULARITY_LABEL: Record<MetaGranularity, string> = {
  HALF_HOUR: "Half-hourly",
  DAY: "Daily",
  MONTH: "Monthly",
};

/** Meta sends UNIX seconds, so the bucket label has to multiply before it reads. */
function metaTimeLabel(ts: number, granularity: MetaGranularity): string {
  const d = new Date(ts * 1000);
  if (granularity === "HALF_HOUR")
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (granularity === "MONTH")
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function metaCategory(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, " ");
}

function metaDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  const hours = Math.floor(mins / 60);
  const rest = mins % 60;
  return rest > 0 ? `${hours}h ${rest}m` : `${hours}h`;
}

function readSavedGranularity(): MetaGranularity {
  const saved = localStorage.getItem("meta_insights_granularity");
  return saved === "HALF_HOUR" || saved === "DAY" || saved === "MONTH" ? saved : "DAY";
}

function readSavedTab(): MetaAnalyticsType {
  const saved = localStorage.getItem("meta_insights_active_tab");
  return saved === "analytics" ||
    saved === "pricing_analytics" ||
    saved === "template_analytics" ||
    saved === "call_analytics"
    ? saved
    : "analytics";
}

export function MetaInsightsView() {
  const toast = useToast();
  const { preset, setPreset, draft, setDraft, apply, range } = useRange({
    initial: "30days",
    presetKey: "meta_insights_time_range",
    customKey: "meta_insights_custom_range",
  });

  const [granularity, setGranularityState] = useState<MetaGranularity>(readSavedGranularity);
  const [accountId, setAccountIdState] = useState(
    () => localStorage.getItem("meta_insights_account_id") ?? "all"
  );
  const [tab, setTabState] = useState<MetaAnalyticsType>(readSavedTab);
  const [version, setVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");

  const setGranularity = (next: MetaGranularity) => {
    setGranularityState(next);
    localStorage.setItem("meta_insights_granularity", next);
  };
  const setAccountId = (next: string) => {
    setAccountIdState(next);
    localStorage.setItem("meta_insights_account_id", next);
  };
  const setTab = (next: MetaAnalyticsType) => {
    setTabState(next);
    localStorage.setItem("meta_insights_active_tab", next);
  };

  const accountsQuery = useAsync(
    () => http.get<MetaAccountsResponse>("/analytics/meta/accounts"),
    []
  );

  const query = useAsync(
    () =>
      http.get<MetaAnalyticsResponse>(
        `/analytics/meta${qs({
          analytics_type: tab,
          start: range.from,
          end: range.to,
          granularity,
          account_id: accountId === "all" ? "" : accountId,
        })}`
      ),
    [tab, range.from, range.to, granularity, accountId, version]
  );

  const results = query.data?.accounts ?? [];

  const refresh = async () => {
    setRefreshing(true);
    try {
      await http.post<{ message?: string }>("/analytics/meta/refresh");
      toast.push("Analytics cache cleared", "good");
      setVersion((v) => v + 1);
    } catch (err) {
      toast.push(errText(err, "Could not refresh the cache"), "critical");
    } finally {
      setRefreshing(false);
    }
  };

  // ---- aggregations, one per tab ------------------------------------------
  const messaging = useMemo(() => {
    const byTime = new Map<number, { sent: number; delivered: number }>();
    let sent = 0;
    let delivered = 0;
    for (const account of results) {
      for (const p of account.data?.analytics?.data_points ?? []) {
        sent += p.sent;
        delivered += p.delivered;
        const prev = byTime.get(p.start) ?? { sent: 0, delivered: 0 };
        byTime.set(p.start, { sent: prev.sent + p.sent, delivered: prev.delivered + p.delivered });
      }
    }
    const series = [...byTime.entries()].sort((a, b) => a[0] - b[0]);
    return { sent, delivered, series };
  }, [results]);

  const pricing = useMemo(() => {
    const byCategory = new Map<string, { volume: number; cost: number }>();
    const byCountry = new Map<string, { volume: number; cost: number }>();
    const paidByCategory = new Map<string, number>();
    const free = { total: 0, customerService: 0, entryPoint: 0 };
    let paidTotal = 0;
    let volume = 0;
    let cost = 0;

    for (const account of results) {
      for (const p of account.data?.pricing_analytics?.data_points ?? []) {
        const v = p.volume || 0;
        const c = p.cost || 0;
        volume += v;
        cost += c;

        const category = p.pricing_category || "OTHER";
        const cat = byCategory.get(category) ?? { volume: 0, cost: 0 };
        byCategory.set(category, { volume: cat.volume + v, cost: cat.cost + c });

        const type = p.pricing_type || "OTHER";
        if (type === "FREE_CUSTOMER_SERVICE") {
          free.total += v;
          free.customerService += v;
        } else if (type === "FREE_ENTRY_POINT") {
          free.total += v;
          free.entryPoint += v;
        } else {
          paidTotal += v;
          paidByCategory.set(category, (paidByCategory.get(category) ?? 0) + v);
        }

        const country = p.country || "UNKNOWN";
        const cc = byCountry.get(country) ?? { volume: 0, cost: 0 };
        byCountry.set(country, { volume: cc.volume + v, cost: cc.cost + c });
      }
    }
    return { volume, cost, byCategory, byCountry, paidByCategory, paidTotal, free };
  }, [results]);

  const templates = useMemo(() => {
    const names: Record<string, string> = {};
    for (const account of results) Object.assign(names, account.template_names ?? {});

    const byTemplate = new Map<
      string,
      Omit<TemplateRow, "id" | "templateId" | "name" | "deliveryRate" | "readRate">
    >();
    const totals = { sent: 0, delivered: 0, read: 0, replied: 0, clicked: 0, cost: 0 };

    for (const account of results) {
      for (const p of account.data?.template_analytics?.data_points ?? []) {
        const clicked = (p.clicked ?? []).reduce((sum, c) => sum + (c.count || 0), 0);
        // Meta reports several cost kinds per template; only amount_spent is the
        // money actually charged.
        const spent = (p.cost ?? []).find((c) => c.type === "amount_spent")?.value ?? 0;
        const replied = p.replied ?? 0;

        totals.sent += p.sent;
        totals.delivered += p.delivered;
        totals.read += p.read;
        totals.replied += replied;
        totals.clicked += clicked;
        totals.cost += spent;

        const prev =
          byTemplate.get(p.template_id) ??
          { sent: 0, delivered: 0, read: 0, replied: 0, clicked: 0, cost: 0 };
        byTemplate.set(p.template_id, {
          sent: prev.sent + p.sent,
          delivered: prev.delivered + p.delivered,
          read: prev.read + p.read,
          replied: prev.replied + replied,
          clicked: prev.clicked + clicked,
          cost: prev.cost + spent,
        });
      }
    }

    const rows: TemplateRow[] = [...byTemplate.entries()]
      .map(([templateId, stats]) => ({
        id: templateId,
        templateId,
        name: names[templateId] ?? templateId,
        ...stats,
        deliveryRate: stats.sent > 0 ? (stats.delivered / stats.sent) * 100 : -1,
        readRate: stats.delivered > 0 ? (stats.read / stats.delivered) * 100 : -1,
      }))
      // The Vue table opened sorted by volume; the shared Table sorts on click,
      // so the initial order is set here instead.
      .sort((a, b) => b.sent - a.sent);

    return { totals, rows };
  }, [results]);

  const calls = useMemo(() => {
    const byType = new Map<string, number>();
    const byDirection = new Map<string, number>();
    let total = 0;
    let duration = 0;
    for (const account of results) {
      for (const p of account.data?.call_analytics?.data_points ?? []) {
        total += p.total_calls;
        duration += p.call_duration;
        const type = p.call_type || "UNKNOWN";
        byType.set(type, (byType.get(type) ?? 0) + p.total_calls);
        const dir = p.call_direction || "UNKNOWN";
        byDirection.set(dir, (byDirection.get(dir) ?? 0) + p.total_calls);
      }
    }
    return { total, duration, byType, byDirection };
  }, [results]);

  const filteredTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    if (!q) return templates.rows;
    return templates.rows.filter(
      (r) => r.name.toLowerCase().includes(q) || r.templateId.toLowerCase().includes(q)
    );
  }, [templates.rows, templateSearch]);

  // A rate carries -1 when its denominator was 0. Nothing was sent, so there is
  // no delivery rate; printing 0.0% would read as total delivery failure.
  const rateCell = (value: number) =>
    value < 0 ? (
      <span className="muted">not measured</span>
    ) : (
      <span className="num">{value.toFixed(1)}%</span>
    );

  const templateColumns: Column<TemplateRow>[] = [
    {
      key: "name",
      header: "Template",
      sortable: true,
      render: (r) => (
        <div>
          <div className="strong">{r.name}</div>
          {r.name !== r.templateId && <div className="muted mono">{r.templateId}</div>}
        </div>
      ),
    },
    { key: "sent", header: "Sent", align: "right", sortable: true, render: (r) => <span className="num">{nf.format(r.sent)}</span> },
    { key: "delivered", header: "Delivered", align: "right", sortable: true, render: (r) => <span className="num">{nf.format(r.delivered)}</span> },
    { key: "read", header: "Read", align: "right", sortable: true, render: (r) => <span className="num">{nf.format(r.read)}</span> },
    { key: "replied", header: "Replied", align: "right", sortable: true, render: (r) => <span className="num">{nf.format(r.replied)}</span> },
    { key: "clicked", header: "Clicked", align: "right", sortable: true, render: (r) => <span className="num">{nf.format(r.clicked)}</span> },
    { key: "deliveryRate", header: "Delivery %", align: "right", sortable: true, render: (r) => rateCell(r.deliveryRate) },
    { key: "readRate", header: "Read %", align: "right", sortable: true, render: (r) => rateCell(r.readRate) },
    {
      key: "cost",
      header: "Cost",
      align: "right",
      sortable: true,
      render: (r) => <span className="num">{r.cost > 0 ? money.format(r.cost) : "—"}</span>,
    },
  ];

  const loadingBlock = (
    <div className="grid c3">
      {[0, 1, 2].map((i) => (
        <div className="card" key={i}>
          <SkeletonRows n={2} cols={2} />
        </div>
      ))}
    </div>
  );

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Meta insights</h1>
          <div className="sub">Messaging, pricing, template and call reporting from the Meta API.</div>
        </div>
        <div className="page-actions">
          <select
            value={accountId}
            aria-label="WhatsApp account"
            style={{ width: 180 }}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="all">All accounts</option>
            {(accountsQuery.data?.accounts ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <select
            value={granularity}
            aria-label="Granularity"
            style={{ width: 140 }}
            onChange={(e) => setGranularity(e.target.value as MetaGranularity)}
          >
            {(Object.keys(GRANULARITY_LABEL) as MetaGranularity[]).map((g) => (
              <option key={g} value={g}>
                {GRANULARITY_LABEL[g]}
              </option>
            ))}
          </select>
          <RangeControls
            preset={preset}
            onPreset={setPreset}
            draft={draft}
            onDraft={setDraft}
            onApply={apply}
          />
          <button className="btn" disabled={refreshing} onClick={() => void refresh()}>
            {refreshing ? <Spinner label="Refreshing" /> : "⟲"} Refresh
          </button>
          {query.data?.cached && <Badge tone="info">Cached</Badge>}
        </div>
      </div>

      {query.error && (
        <Banner tone="critical" traceId={query.error.traceId}>
          Could not load Meta insights: {query.error.message}
        </Banner>
      )}
      {accountsQuery.error && (
        <Banner tone="warning" traceId={accountsQuery.error.traceId}>
          The account list did not load: {accountsQuery.error.message}
        </Banner>
      )}
      {query.data?.adjusted_granularity && (
        <Banner tone="info">
          Meta rejected {query.data.original_granularity} buckets for this window, so the report is
          drawn at {query.data.adjusted_granularity}.
        </Banner>
      )}
      {query.data?.message && <Banner tone="info">{query.data.message}</Banner>}

      <Tabs tabs={META_TABS} active={tab} onChange={(id) => setTab(id as MetaAnalyticsType)} />

      <div style={{ marginTop: "var(--sp-4)" }}>
        {query.loading ? (
          loadingBlock
        ) : tab === "analytics" ? (
          messaging.series.length === 0 ? (
            <Empty title="No messaging data" body="Nothing was reported for this period." icon="✉" />
          ) : (
            <>
              <div className="grid c3" style={{ marginBottom: "var(--sp-4)" }}>
                <StatTile label="Messages sent" value={messaging.sent} />
                <StatTile label="Messages delivered" value={messaging.delivered} />
                {/* Nothing sent means there is no delivery rate to report. */}
                {messaging.sent > 0 ? (
                  <StatTile
                    label="Delivery rate"
                    value={`${((messaging.delivered / messaging.sent) * 100).toFixed(1)}%`}
                  />
                ) : (
                  <NotMeasuredTile label="Delivery rate" why="no message was sent in this period" />
                )}
              </div>
              <div className="card">
                <LineChartCard
                  title="Message delivery over time"
                  yLabel="Messages"
                  series={[
                    {
                      name: "Sent",
                      points: messaging.series.map(([t, v]) => ({
                        x: metaTimeLabel(t, granularity),
                        y: v.sent,
                      })),
                    },
                    {
                      name: "Delivered",
                      points: messaging.series.map(([t, v]) => ({
                        x: metaTimeLabel(t, granularity),
                        y: v.delivered,
                      })),
                    },
                  ]}
                />
              </div>
            </>
          )
        ) : tab === "pricing_analytics" ? (
          pricing.byCategory.size === 0 ? (
            <Empty title="No pricing data" body="Nothing was reported for this period." icon="◎" />
          ) : (
            <>
              <div className="grid c2" style={{ marginBottom: "var(--sp-4)" }}>
                <StatTile label="Total messages" value={pricing.volume} />
                <StatTile label="Total cost" value={money.format(pricing.cost)} />
              </div>
              <div className="grid c2" style={{ marginBottom: "var(--sp-4)" }}>
                <div className="card">
                  {/* Volume is messages and cost is dollars, so they are two
                      charts. A shared axis would compare a count with a price. */}
                  <BarChartCard
                    title="Message volume by category"
                    yLabel="Messages"
                    data={[...pricing.byCategory.entries()].map(([cat, v]) => ({
                      label: metaCategory(cat),
                      value: v.volume,
                    }))}
                  />
                </div>
                <div className="card">
                  <BarChartCard
                    title="Cost by category"
                    yLabel="USD"
                    data={[...pricing.byCategory.entries()].map(([cat, v]) => ({
                      label: metaCategory(cat),
                      value: v.cost,
                    }))}
                  />
                </div>
              </div>
              <div className="grid c2">
                <div className="card">
                  <h3>Free messages</h3>
                  <div className="col" style={{ marginTop: "var(--sp-3)" }}>
                    <div className="row">
                      <span>Customer service</span>
                      <span className="spacer" />
                      <strong className="num">{nf.format(pricing.free.customerService)}</strong>
                    </div>
                    <div className="row">
                      <span>Entry point</span>
                      <span className="spacer" />
                      <strong className="num">{nf.format(pricing.free.entryPoint)}</strong>
                    </div>
                    <div className="row">
                      <span className="strong">Total free</span>
                      <span className="spacer" />
                      <strong className="num">{nf.format(pricing.free.total)}</strong>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <h3>Paid messages</h3>
                  {pricing.paidByCategory.size === 0 ? (
                    <div className="muted" style={{ marginTop: "var(--sp-3)" }}>
                      No paid messages in this period.
                    </div>
                  ) : (
                    <div className="col" style={{ marginTop: "var(--sp-3)" }}>
                      {[...pricing.paidByCategory.entries()].map(([cat, count]) => (
                        <div className="row" key={cat}>
                          <span>{metaCategory(cat)}</span>
                          <span className="spacer" />
                          <strong className="num">{nf.format(count)}</strong>
                        </div>
                      ))}
                      <div className="row">
                        <span className="strong">Total paid</span>
                        <span className="spacer" />
                        <strong className="num">{nf.format(pricing.paidTotal)}</strong>
                      </div>
                    </div>
                  )}
                </div>
                <div className="card">
                  <h3>Cost by category</h3>
                  <div className="col" style={{ marginTop: "var(--sp-3)" }}>
                    {[...pricing.byCategory.entries()].map(([cat, v]) => (
                      <div className="row" key={cat}>
                        <span>{metaCategory(cat)}</span>
                        <span className="spacer" />
                        <strong className="num">{money.format(v.cost)}</strong>
                      </div>
                    ))}
                    <div className="row">
                      <span className="strong">Total cost</span>
                      <span className="spacer" />
                      <strong className="num">{money.format(pricing.cost)}</strong>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <h3>By country</h3>
                  <div className="col" style={{ marginTop: "var(--sp-3)" }}>
                    {[...pricing.byCountry.entries()].map(([country, v]) => (
                      <div className="row" key={country}>
                        <span>{country}</span>
                        <span className="spacer" />
                        <strong className="num">{nf.format(v.volume)} msgs</strong>
                        <span className="muted num">{money.format(v.cost)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )
        ) : tab === "template_analytics" ? (
          templates.rows.length === 0 ? (
            <Empty
              title="No template analytics"
              body="Meta reports template metrics for the last 90 days only."
              icon="▤"
            />
          ) : (
            <>
              <div className="grid c4" style={{ marginBottom: "var(--sp-4)" }}>
                <StatTile label="Sent" value={templates.totals.sent} />
                <StatTile label="Delivered" value={templates.totals.delivered} />
                <StatTile label="Read" value={templates.totals.read} />
                <StatTile label="Replied" value={templates.totals.replied} />
                <StatTile label="Clicked" value={templates.totals.clicked} />
                <StatTile
                  label="Total cost"
                  value={templates.totals.cost > 0 ? money.format(templates.totals.cost) : "—"}
                />
              </div>
              <div className="card flush">
                <div className="card-head">
                  <h3>Template performance</h3>
                  <Search
                    value={templateSearch}
                    onChange={setTemplateSearch}
                    placeholder="Search templates…"
                    ariaLabel="Search templates"
                  />
                </div>
                <Table
                  columns={templateColumns}
                  rows={filteredTemplates}
                  empty={
                    templateSearch
                      ? "No templates match that search"
                      : "No template data for this period"
                  }
                />
              </div>
            </>
          )
        ) : calls.total === 0 && calls.byType.size === 0 ? (
          <Empty title="No call analytics" body="Nothing was reported for this period." icon="☎" />
        ) : (
          <>
            <div className="grid c2" style={{ marginBottom: "var(--sp-4)" }}>
              <StatTile label="Total calls" value={calls.total} />
              <StatTile label="Total duration" value={metaDuration(calls.duration)} />
            </div>
            <div className="grid c2">
              <div className="card">
                <h3>Calls by type</h3>
                <div style={{ marginTop: "var(--sp-3)" }}>
                  <KeyCount
                    rows={[...calls.byType.entries()].map(([name, count]) => ({
                      name: metaCategory(name),
                      count,
                    }))}
                    empty="No call data"
                  />
                </div>
              </div>
              <div className="card">
                <h3>Calls by direction</h3>
                <div style={{ marginTop: "var(--sp-3)" }}>
                  <KeyCount
                    rows={[...calls.byDirection.entries()].map(([name, count]) => ({
                      name: metaCategory(name),
                      count,
                    }))}
                    empty="No call data"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
